import React, { useCallback, useRef } from 'react'
import { createContextWithHook } from '@chilipiper/utils'
import { Key, ListKeyboardDelegate } from 'react-aria'
import { Collection, ListState, Node } from 'react-stately'
import { DynamicCascadeItem, DynamicItem, DynamicSimpleItem } from './dynamicItemUtils'

type CascadeListFocuRef = {
  ref: (
    data: {
      collection: Collection<Node<DynamicItem>>
      keyboardDelegate: ListKeyboardDelegate<unknown>
      selectionManager: ListState<unknown>['selectionManager']
    },
    path: string
  ) => () => void
  queueFocus: (path: string, vlue?: Key) => void
  getFocusedPath: () => undefined | string
  observeCollection: (path: string, callback: () => void) => void
  getCollection: (path: string) => (DynamicSimpleItem | DynamicCascadeItem)[] | undefined
  getSelectionManager: (path: string) => ListState<unknown>['selectionManager'] | undefined
  getKeyboardDelegate: (path: string) => ListKeyboardDelegate<unknown>
}

const { useCascadeListFocusContext, CascadeListFocusBaseProvider } =
  createContextWithHook<ReturnType<typeof useCascadeListFocusRef>>()('CascadeListFocus')

export const CascadeListFocusProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    queueFocus,
    ref,
    getCollection,
    observeCollection,
    getSelectionManager,
    getKeyboardDelegate,
    getFocusedPath,
  } = useCascadeListFocusRef()

  return (
    <CascadeListFocusBaseProvider
      value={{
        getCollection,
        getSelectionManager,
        observeCollection,
        queueFocus,
        ref,
        getKeyboardDelegate,
        getFocusedPath,
      }}
    >
      {children}
    </CascadeListFocusBaseProvider>
  )
}

export const useCascadeListFocus = () => {
  return useCascadeListFocusContext()
}

const useCascadeListFocusRef = (): CascadeListFocuRef => {
  const ref = useRef<{
    collectionsListener?: { [path: string]: () => void }
    data: {
      [path: string]: {
        collection: Collection<Node<DynamicItem>>
        keyboardDelegate: ListKeyboardDelegate<unknown>
        selectionManager: ListState<unknown>['selectionManager']
      }
    }
    queuedAction?: { path: string; type: 'initial' } | { path: string; type: 'focus'; value: Key }
  }>({ data: {} })
  return {
    ref: useCallback(
      (
        data: {
          collection: Collection<Node<DynamicItem>>
          keyboardDelegate: ListKeyboardDelegate<unknown>
          selectionManager: ListState<unknown>['selectionManager']
        },
        path: string
      ) => {
        const currentFocusedSelectionManagers = Object.values(ref.current.data)
          .map(data => data.selectionManager)
          .filter(selectionManager => selectionManager.isFocused)
        ref.current.data[path] = data
        if (ref.current.queuedAction?.path === path) {
          currentFocusedSelectionManagers.forEach(selectionManager =>
            selectionManager.setFocused(false)
          )
          data.selectionManager.setFocused(true)
          const focusKey =
            ref.current.queuedAction.type === 'focus'
              ? ref.current.queuedAction.value
              : (data.keyboardDelegate.getFirstKey() ?? data.collection.getFirstKey())
          if (focusKey) {
            data.selectionManager.setFocusedKey(focusKey)
          }
          ref.current.queuedAction = undefined
        } else if (currentFocusedSelectionManagers.length && data.selectionManager.isFocused) {
          data.selectionManager.setFocused(false)
        }
        if (ref.current.collectionsListener?.[path]) {
          ref.current.collectionsListener[path]()
          delete ref.current.collectionsListener[path]
        }
        return () => {
          delete ref.current.data[path]
        }
      },
      []
    ),
    queueFocus: useCallback((path: string, value?: Key) => {
      if (ref.current.data[path]) {
        Object.values(ref.current.data)
          .map(data => data.selectionManager)
          .filter(
            selectionManager =>
              selectionManager.isFocused &&
              selectionManager !== ref.current.data[path].selectionManager
          )
          .forEach(selectionManager => selectionManager.setFocused(false))
        ref.current.data[path].selectionManager.setFocused(true)
        const focusKey =
          value ??
          ref.current.data[path].selectionManager.firstSelectedKey ??
          ref.current.data[path].keyboardDelegate.getFirstKey() ??
          ref.current.data[path].collection.getFirstKey()
        if (focusKey) {
          ref.current.data[path].selectionManager.setFocusedKey(focusKey)
        }
      } else {
        ref.current.queuedAction = value
          ? { type: 'focus', value, path }
          : { type: 'initial', path }
      }
    }, []),
    getFocusedPath: useCallback(() => {
      return Object.entries(ref.current.data).find(
        ([, data]) => data.selectionManager.isFocused
      )?.[0]
    }, []),
    observeCollection: useCallback((path: string, callback: () => void) => {
      if (ref.current.data[path]) {
        callback()
      } else {
        ref.current.collectionsListener = { [path]: callback }
      }
    }, []),
    getCollection: useCallback((path: string) => {
      if (ref.current.data[path]) {
        return [...ref.current.data[path].collection]
          .map(node => node.value)
          .filter((item): item is DynamicItem => !!item)
          .flatMap(item => {
            if (item.itemType === 'section') {
              return item.children
            }
            return [item]
          })
      }
      return undefined
    }, []),
    getSelectionManager: useCallback((path: string) => {
      return ref.current.data[path]?.selectionManager
    }, []),
    getKeyboardDelegate: useCallback((path: string) => {
      return ref.current.data[path]?.keyboardDelegate
    }, []),
  }
}
